import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils'; // Assuming this utility exists, otherwise standard className
// If @/lib/utils/cn doesn't exist, I'll fallback to standard or create it. 
// Given the file list, I didn't see a lib folder, but it's common. I'll check first or just use template literals.
import handCursor from '@/assets/hand.png';

interface PhoneMockupProps {
    children?: React.ReactNode;
    className?: string;
    showCursor?: boolean;
}

const PhoneMockup: React.FC<PhoneMockupProps> = ({ children, className, showCursor = true }) => {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setMousePos({
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                });
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener('mousemove', handleMouseMove);
            container.addEventListener('mouseenter', () => setIsHovering(true));
            container.addEventListener('mouseleave', () => setIsHovering(false));

            return () => {
                container.removeEventListener('mousemove', handleMouseMove);
                container.removeEventListener('mouseenter', () => setIsHovering(true));
                container.removeEventListener('mouseleave', () => setIsHovering(false));
            };
        }
    }, []);

    return (
        <div
            ref={containerRef}
            className={cn(`relative mx-auto bg-white/50 dark:bg-black/50 p-3.5 rounded-[3rem] h-[600px] w-[300px] dark:shadow-2xl dark:shadow-[-20px_20px_60px_rgba(0,0,0,0.4)] ${showCursor ? "cursor-none [&_*]:cursor-none" : ""}`, className)}
        >


            <div className="h-[32px] w-[3px] bg-gray-100 dark:bg-black absolute -start-[3px] top-[72px] rounded-s-lg"></div>
            <div className="h-[46px] w-[3px] bg-gray-100 dark:bg-black absolute -start-[3px] top-[124px] rounded-s-lg"></div>
            <div className="h-[46px] w-[3px] bg-gray-100 dark:bg-black absolute -start-[3px] top-[178px] rounded-s-lg"></div>
            <div className="h-[64px] w-[3px] bg-gray-100 dark:bg-black absolute -end-[3px] top-[142px] rounded-e-lg"></div>
            <div className="rounded-[2rem] overflow-hidden w-full h-full bg-white dark:bg-black relative z-10">
                {/* Dynamic Island Area (simplified for now, just the notch visual above is mostly enough) */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[120px] h-[30px] bg-white dark:bg-black rounded-full z-30 flex items-center justify-center pointer-events-none">
                    {/* Dynamic Island Content could go here */}
                    <div className="w-2 h-2 rounded-full bg-white dark:bg-black ml-auto mr-3"></div>
                </div>

                {/* Screen Content */}
                <div className="w-full h-full rounded-[2rem] bg-white text-black overflow-hidden relative">
                    {children}

                    {isHovering && showCursor && (
                        <img
                            src={handCursor}
                            alt="cursor"
                            className="absolute z-[100] pointer-events-none w-10 h-10 drop-shadow-md transition-none origin-top-left"
                            style={{
                                left: `${mousePos.x}px`,
                                top: `${mousePos.y}px`,
                                transform: 'translate(-50%, -50%)' // offset slightly to align the pointing finger
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PhoneMockup;
